import { sectionLabels } from "../section-labels";
/**
 * The menu itself — the reason the page exists and the only part of it a
 * search engine should find.
 *
 * `locked` + `criticalForSeo`: a restaurant's menu page IS the page people
 * search for (decision D14 keeps it indexable for exactly that reason), so it
 * can be neither removed nor hidden.
 *
 * ## No cart lives here
 *
 * This section renders a menu, never a shop. There is no add-to-basket and no
 * checkout link, because a `RESTAURANT` store does not transact through the
 * storefront at all — `StorefrontCommerceGuard` rejects every public cart and
 * order route for it server-side. The ordering flow is a different surface
 * entirely: the QR page at `/{locale}/{storeSlug}/t/{tableCode}`, which sends
 * a staff-approved REQUEST rather than a charge.
 *
 * ## Sections come from the store, not from here
 *
 * The groupings are the store's own menu sections (`store_categories`,
 * filtered to the active ones server-side). They are not authored here and
 * there is no section-picker field: a menu whose headings are typed into the
 * theme would drift from the one the till prints the moment either changed.
 */
export const restaurantMenuSchema = {
    type: "restaurant-menu",
    name: sectionLabels["restaurant-menu"],
    description: {
        ar: "أطباق المطعم مقسّمة على أقسام المنيو.",
        en: "The dishes, grouped under the restaurant's own menu sections.",
    },
    locked: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    availableOnStoreKinds: ["RESTAURANT"],
    settings: [
        {
            /**
             * The DESKTOP band's title — "THE LINEUP", "Tonight's menu".
             *
             * The desktop artboards do not list every section on the home page: they
             * show ONE curated band with a handful of dishes and a link to the full
             * menu. That band is not a category, so its name cannot be derived from
             * one; without a field for it the page had to fall back to listing every
             * section, which is the phone's layout on a desktop.
             *
             * Empty ⇒ the band falls back to the first section's own name.
             */
            id: "featuredTitle",
            type: "text",
            i18n: true,
            label: { ar: "عنوان القسم المميز", en: "Featured band title" },
            validate: { max: 40 },
        },
        {
            id: "layout",
            type: "select",
            label: { ar: "شكل العرض", en: "Layout" },
            helpText: {
                ar: "الأطباق تتعرض في سطور طويلة ولا كروت جنب بعض.",
                en: "Show dishes as full-width rows, or as cards side by side.",
            },
            options: [
                // The row is the default because it is the only one that reads well
                // with a long dish description, and a restaurant menu is mostly prose.
                { value: "rows", label: { ar: "سطور", en: "Rows" } },
                { value: "cards", label: { ar: "كروت", en: "Cards" } },
                // Numbered, image-light, for a menu read as a list rather than browsed
                // — the treatment KOYO and OTTO share.
                { value: "listing", label: { ar: "قائمة مرقّمة", en: "Numbered listing" } },
            ],
            default: "rows",
        },
        {
            id: "showImages",
            type: "checkbox",
            label: { ar: "إظهار صور الأطباق", en: "Show dish photos" },
            helpText: {
                ar: "الأطباق اللي مالهاش صورة بتتعرض من غيرها.",
                en: "Dishes with no photo simply render without one.",
            },
            default: true,
        },
        {
            id: "showDescriptions",
            type: "checkbox",
            label: { ar: "إظهار وصف الأطباق", en: "Show dish descriptions" },
            default: true,
        },
        {
            /**
             * Sticky per-section chips that jump down the page. Worth a control
             * because it is wrong at both ends: a three-section menu does not need
             * navigation, and a thirty-section one is unusable without it.
             */
            id: "showSectionNav",
            type: "checkbox",
            label: { ar: "شريط التنقل بين الأقسام", en: "Section jump bar" },
            default: true,
        },
        {
            id: "priceStyle",
            type: "select",
            label: { ar: "شكل السعر", en: "Price style" },
            options: [
                { value: "plain", label: { ar: "عادي", en: "Plain" } },
                { value: "accent", label: { ar: "بلون مميز", en: "In the accent colour" } },
                // Right-aligned with a leader rule running to it — the printed-menu
                // convention OTTO and BRÜ are drawn against.
                { value: "leader", label: { ar: "بخط موصّل", en: "With a leader rule" } },
            ],
            default: "plain",
        },
    ],
};
//# sourceMappingURL=restaurant-menu.js.map